import React from "react";
import Link from "next/link";
import { PICK_TYPES } from "src/common-services";

import styles from "./styles.scss";
import { renderRating } from "../../../../services/ValueDataService";
import { renderDrawerText } from "../../../../services/DisplayService";
import MarketingVideo from "src/components/MarketingVideo";
import { getPeriodFilter } from "../";

export interface RatingTrendProps {
  game: any;
  filterKey: string;
  hasAccess: boolean;
  renderUpgradeButton: any;
}

function RatingTrend(props: RatingTrendProps) {
  const { game, filterKey, hasAccess, renderUpgradeButton } = props;
  const { homeTeam, awayTeam } = game;
  const periodFilter = getPeriodFilter(filterKey);
  const trend = game[`max${periodFilter}Trend`];
  const newTrend = game[`maxNew${periodFilter}Trend`];
  const hasTrend = trend && trend.displayRating;
  const hasNewTrend = newTrend && newTrend.displayRating;
  const hasFavorite = newTrend && newTrend.favoriteText && newTrend.favoriteData;
  const noDisplayMessage = "No Trend";

  const noAwayLine = game[`current${periodFilter}AwayDisplay`] === "--";
  const noHomeLine = game[`current${periodFilter}HomeDisplay`] === "--";
  const hasCurrentLine = !noAwayLine && !noHomeLine;

  return (
    <div className="rating-trend__container">
      {hasAccess ? (
        <>
          {hasFavorite ? (
            <div className="rating-trend__favorite">
              {`${getFavoriteName(filterKey, newTrend, homeTeam, awayTeam)} ${newTrend.favoriteData}`}
            </div>
          ) : null}
          <div className="rating-trend__section">
            {hasNewTrend && hasCurrentLine ? (
              <div className="rating-trend__rating">{renderRating(newTrend, false)}</div>
            ) : (
              <div className="rating-trend__title">Star Rating</div>
            )}
            {hasNewTrend && hasCurrentLine ? (
              renderDrawerText(game, filterKey, "rating-trend__title", "rating-trend__body")
            ) : (
              <div className="rating-trend__body">{noDisplayMessage}</div>
            )}
          </div>
          <div className="rating-trend__section">
            <div className="rating-trend__title">{hasTrend ? trend.title : "Trend Rating"}</div>
            <div className="rating-trend__body">{hasTrend ? trend.description : noDisplayMessage}</div>
          </div>
        </>
      ) : (
        <>
          <div className="rating-trend__favorite">{getFreeTitle(game, filterKey)}</div>
          <div className="rating-trend__section">
            <div className="rating-trend__title--upgrade">
              {`See who our recommended best bet is for ${awayTeam.fullName} vs. ${homeTeam.fullName}`}
            </div>
            <div className="rating-trend__body--upgrade">
              {`Subscribe now to access ${awayTeam.fullName} vs. ${homeTeam.fullName} predictions and more:`}
            </div>
            <div className="rating-trend__feature-list">
              <div className="rating-trend__title">- Best Bets</div>
              <div className="rating-trend__title">- Pro Betting Trends</div>
              <div className="rating-trend__title">- Public Betting Consensus</div>
            </div>
            <div className="rating-trend__upgrade-info">
              {renderUpgradeButton()}
              <div className="rating-trend__info-links">
                <Link href="/how-it-works" legacyBehavior>
                  <a className="rating-trend__info-link">More info about Best Bets</a>
                </Link>
                <MarketingVideo className="rating-trend__marketing-video" />
              </div>
            </div>
          </div>
        </>
      )}
      <style jsx global>
        {styles}
      </style>
    </div>
  );
}

function getFreeTitle(game: any, filterKey: string) {
  const periodFilter = getPeriodFilter(filterKey);
  if (periodFilter.includes("spread")) {
    const { homeTeam, awayTeam } = game;
    const pickType = PICK_TYPES[filterKey];
    const awayData = game[`current${periodFilter}AwayData`];
    const homeData = game[`current${periodFilter}HomeData`];
    const team = awayData === homeData ? "Away" : awayData < homeData ? "Away" : "Home";
    return `${awayTeam.fullName} vs. ${homeTeam.fullName} (${game[`current${periodFilter}${team}Display`] || "--"}) (${
      game[`current${pickType.linePeriodKey}${team}PriceDisplay`] || "--"
    })`;
  } else if (periodFilter.includes("moneyline")) {
    const { homeTeam, awayTeam } = game;
    return `${awayTeam.fullName} (${game[`current${periodFilter}AwayDisplay`]}) vs. ${homeTeam.fullName} (${
      game[`current${periodFilter}HomeDisplay`] || "--"
    })`;
  } else if (periodFilter.includes("total")) {
    const pickType = PICK_TYPES[filterKey];
    return (
      <div>
        {`Over Under: ${game[`current${periodFilter}AwayDisplay`] || "--"}`}
        <br />
        <br />
        <span style={{ fontSize: 13 }}>
          {`Over (${game[`current${pickType.linePeriodKey}OverPriceDisplay`] || "--"})`}
          <br />
          {`Under (${game[`current${pickType.linePeriodKey}UnderPriceDisplay`] || "--"})`}
        </span>
      </div>
    );
  }
  return "";
}

function getFavoriteName(filterKey: string, trend: any, homeTeam: any, awayTeam: any): string {
  if (filterKey.includes("total") || trend.favoriteText === "Draw") {
    return trend.favoriteText;
  }
  return trend.favoriteText === homeTeam.preferredAbbreviation ? homeTeam.fullName : awayTeam.fullName;
}

export default RatingTrend;
